/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useState } from "react"
import { AppStateProvider, userAgentParser } from "./components/AppStateProvider/AppStateProvider"
import { MainContent } from "./components/MainContent/MainContent"
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles"
import { Dialog } from "@mui/material"
import theme from "@medaica/common/theme"
import useGlobalContext from "@medaica/common/hooks/global-context"
import { NavigationItem } from "@medaica/common/types/ui"
import AVStore from "@medaica/common/views/exam/virtual-exam/stores/av-store"
import { FailureReason, PlatformUnsupported } from "@medaica/common/components/compatibility-check"
import CompatibilityCheckFailureMessage from "@medaica/common/components/compatibility-check-failure-message"
import MinimalLayout from "@medaica/common/layouts/minimal-layout"

const CompatibilityTestView = ({ footerNavigation }: { footerNavigation?: NavigationItem[] }): ReactElement => {
  const { medaicaApiService } = useGlobalContext()
  const [platformCheckDismissed, setPlatformCheckDismissed] = useState(false)
  // Same platform gate as /live-exam (CompatibilityCheck): the test exercises a virtual exam,
  // so a platform that can't run one (any mobile browser; desktop Safari/Firefox) gets the
  // identical unsupported dialog → Compatibility Error flow and the wizard never mounts.
  const platformSupported = AVStore.isPlatformSupported()

  let content: ReactElement
  if (platformSupported) {
    content = (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <AppStateProvider
            accessTokenProvider={() => medaicaApiService.twilio.getCompatibilityTestAccessToken()}
            userAgentParser={userAgentParser}
          >
            <div className="h-full overflow-hidden relative bg-gray-100">
              <MainContent />
            </div>
          </AppStateProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    )
  } else if (platformCheckDismissed) {
    // Same content as the virtual exam's CompatibilityCheckFailure, without its own MinimalLayout —
    // the route-level layout below already provides the header and this route's footer.
    content = (
      <div className="w-3/6 mx-auto">
        <h2 className="text-3xl font-bold mb-6">Compatibility Error</h2>
        <CompatibilityCheckFailureMessage failureReason={FailureReason.platformUnsupported} />
      </div>
    )
  } else {
    content = (
      <Dialog open disableEscapeKeyDown>
        <PlatformUnsupported onClose={() => setPlatformCheckDismissed(true)} />
      </Dialog>
    )
  }

  return (
    // One layout around every state, so the branded header/footer are present on the blocked path
    // too. mainClassName="w-full": the wizard is a full-bleed page — it needs the whole main area
    // (h-full above) for its centered panes; the failure content keeps the default main-centered.
    <MinimalLayout footerNavigation={footerNavigation} mainClassName={platformSupported ? "w-full" : undefined}>
      {content}
    </MinimalLayout>
  )
}

export default CompatibilityTestView
